import React from 'react'
import { Typography, Button, Box, Chip, Tooltip } from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'

import { feedback } from '../../core/feedback'
import { requests } from '../../core/ajax'
import { downloadFile } from '../../core/utils'
import { FileUploaderAgnostic } from '../file-uploader-agnostic'
import { UploadTooltip } from '../upload-tooltip'

/**
 * @typedef {object} FileValue
 * @property {string} data - El archivo codificado en Base64.
 * @property {string} name - El nombre del archivo sin extensión.
 * @property {string} type - La extensión del archivo.
 */

/**
 * @typedef {object} CurrentDocument
 * @property {string | number} id - El ID del documento en el servidor.
 * @property {string} nombre - El nombre del documento sin extensión.
 * @property {string} extension - La extensión del documento.
 */

/**
 * @typedef {object} FileChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo.
 * @property {FileValue | null} target.value - Los datos del archivo, o `null` si se elimina.
 */

/**
 * @typedef {object} FileUploaderInputProps
 * @property {string} name - El nombre del campo de entrada.
 * @property {FileValue | null} [value] - El valor actual del archivo (archivo local procesado).
 * @property {string} [title] - El título que se muestra sobre el campo de carga.
 * @property {function(FileChangeEvent): void} onChange - La función callback que se ejecuta cuando el archivo cambia o se elimina.
 * @property {object} [fileProps] - Props adicionales a pasar al componente `FileUploaderAgnostic` (ej. `accept`, `maxSize`).
 * @property {boolean} [readOnly=false] - Si es `true`, el componente solo muestra el archivo actual sin permitir cambios.
 * @property {CurrentDocument} [currentDoc] - El documento actual almacenado en el servidor (si existe).
 */

/**
 * Un componente de entrada de archivos con funcionalidad completa de carga, descarga y eliminación.
 * 
 * Permite al usuario seleccionar un archivo local (que se codifica en Base64), descargar archivos
 * tanto locales como del servidor, y eliminar el archivo seleccionado. Muestra el archivo actual
 * como un chip con el nombre del archivo y un icono de adjunto.
 * 
 * Soporta dos tipos de archivos:
 * - **Archivos locales**: Procesados y almacenados en el estado del formulario como Base64.
 * - **Archivos del servidor**: Descargados mediante una petición a la API.
 * 
 * @component
 * @example
 * const MyForm = () => {
 *   const [file, setFile] = useState(null);
 * 
 *   const handleFileChange = (event) => {
 *     setFile(event.target.value);
 *   };
 * 
 *   return (
 *     <FileUploaderInput
 *       name="attachment"
 *       title="Adjuntar Documento"
 *       value={file}
 *       onChange={handleFileChange}
 *       fileProps={{
 *         accept: ['.pdf', '.doc', '.docx'],
 *         maxSize: 10000000, // 10 MB
 *       }}
 *       currentDoc={{
 *         id: 123,
 *         nombre: 'documento_existente',
 *         extension: 'pdf',
 *       }}
 *     />
 *   );
 * };
 * 
 * @param {FileUploaderInputProps} props - Las props del componente FileUploaderInput.
 */
export const FileUploaderInput = (props) => {
  const { name, value, title, onChange, fileProps, readOnly = false } = props
  const { getById } = requests

  const handleFileUploadError = (data) => feedback('error', data.mssg)

  const handleDownloadCloudFile = (id) => {
    getById('/documento', id)
      .then(({ data }) => {
        downloadFile(data['documento'], data['extension'], data['nombre'])
      })
      .catch(() => {
        console.debug('Download document fail')
        feedback('error', 'Error al descargar el documento')
      })
  }

  const handleDownloadLocalFile = ({ data, name }) => {
    const link = document.createElement('a')
    link.target = '_blank'
    link.href = data
    link.download = name
    link.click()
  }

  const handleDeleteFile = () => {
    onChange({ target: { name, value: null } })
  }

  return (
    <>
      {title ? (
        <Box display="flex" flexDirection="horizontal">
          <Typography style={{ marginRight: '0.5em' }} variant="subtitle1" gutterBottom>
            {title}
          </Typography>
          {!readOnly ? <UploadTooltip file={fileProps} /> : null}
        </Box>
      ) : null}
      {!readOnly ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          height={48}
          flexWrap="wrap"
        >
          <FileUploaderAgnostic
            name={name}
            onSuccess={onChange}
            onError={handleFileUploadError}
            required
            {...fileProps}
          >
            {() => (
              <Button component="span" variant="outlined">
                Seleccionar archivo
              </Button>
            )}
          </FileUploaderAgnostic>
        </Box>
      ) : null}
      {value ? (
        <Chip
          classes={{ labelSmall: { overflow: 'hidden', textOverflow: 'ellipsis' } }}
          size="small"
          avatar={<AttachFileIcon />}
          label={
            <Typography
              variant="body2"
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 250 }}
            >
              {value.name + '.' + value.type}
            </Typography>
          }
          onClick={() => handleDownloadLocalFile(value)}
          onDelete={handleDeleteFile}
        />
      ) : props.currentDoc ? (
        <Tooltip
          arrow
          title={
            <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
              Descargar fichero: {props.currentDoc.nombre + '.' + props.currentDoc.extension}
            </Typography>
          }
        >
          <Chip
            size="small"
            avatar={<AttachFileIcon />}
            label={
              <Typography
                variant="body2"
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 250 }}
              >
                {props.currentDoc.nombre + '.' + props.currentDoc.extension}
              </Typography>
            }
            onClick={() => handleDownloadCloudFile(props.currentDoc.id)}
          />
        </Tooltip>
      ) : readOnly ? (
        '--'
      ) : null}
    </>
  )
}
